import { useEffect, useRef, useState } from 'react';
import MentorshipSessionLogTable from './MentorshipSessionLogTable';
import './MentorshipSessionLog.css';
import MentorshipService, {
    MentorshipCampusOption,
    MentorshipCohortSessionLogQuery,
    MentorshipCohortSessionLogRow,
    SESSION_LOG_LOGGED_BY_ROLES,
    SESSION_LOG_PAGE_SIZE_DEFAULT,
    SESSION_LOG_PAGE_SIZES,
} from './MentorshipService';

const SEARCH_DEBOUNCE_MS = 350;

interface MentorshipSessionLogProps {
    cohortId: number | null;
    disabled?: boolean;
    campuses: MentorshipCampusOption[];
    instituteId?: number | null;
}

const MentorshipSessionLog = ({
    cohortId,
    disabled = false,
    campuses,
    instituteId = null,
}: MentorshipSessionLogProps) => {
    const cohortAvailable = cohortId != null && !disabled;
    const [searchInput, setSearchInput] = useState('');
    const [campusId, setCampusId] = useState<number | null>(null);
    const [loggedByRole, setLoggedByRole] = useState('');
    const [ratingFilter, setRatingFilter] = useState<'' | 'rated' | 'unrated'>('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(SESSION_LOG_PAGE_SIZE_DEFAULT);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sessionCount, setSessionCount] = useState(0);
    const [pairCount, setPairCount] = useState(0);
    const [sessions, setSessions] = useState<MentorshipCohortSessionLogRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [exportBusy, setExportBusy] = useState(false);
    const [exportError, setExportError] = useState('');
    const filterKey = `${appliedSearch}|${campusId ?? ''}|${loggedByRole}|${ratingFilter}`;
    const prevFilterKeyRef = useRef(filterKey);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setAppliedSearch(searchInput.trim());
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (cohortId == null || disabled) {
            setSearchInput('');
            setCampusId(null);
            setLoggedByRole('');
            setRatingFilter('');
            setAppliedSearch('');
            setPage(1);
            setPageSize(SESSION_LOG_PAGE_SIZE_DEFAULT);
            setTotal(0);
            setTotalPages(0);
            setSessionCount(0);
            setPairCount(0);
            setSessions([]);
            setErrorMessage('');
            setExportError('');
            setIsLoading(false);
            prevFilterKeyRef.current = '|';
            return;
        }

        if (prevFilterKeyRef.current !== filterKey) {
            prevFilterKeyRef.current = filterKey;
            if (page !== 1) {
                setPage(1);
                return;
            }
        }

        let cancelled = false;
        const query: MentorshipCohortSessionLogQuery = {
            search: appliedSearch,
            campusId,
            loggedByRole: loggedByRole || null,
            rating: ratingFilter || undefined,
            page,
            pageSize,
        };

        const load = async () => {
            setIsLoading(true);
            setErrorMessage('');
            try {
                const result = await MentorshipService.listCohortSessions(cohortId, query, instituteId);
                if (cancelled) return;
                setSessions(result.sessions);
                setPage(result.pagination.page);
                setPageSize(result.pagination.pageSize);
                setTotal(result.pagination.total);
                setTotalPages(result.pagination.totalPages);
                setSessionCount(result.sessionCount);
                setPairCount(result.pairCount);
            } catch (error) {
                if (cancelled) return;
                setSessions([]);
                setTotal(0);
                setTotalPages(0);
                setSessionCount(0);
                setPairCount(0);
                setErrorMessage(MentorshipService.getErrorMessage(error));
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [cohortId, disabled, appliedSearch, campusId, loggedByRole, ratingFilter, filterKey, page, pageSize, instituteId]);

    const handleExport = async () => {
        if (cohortId == null || exportBusy) return;
        setExportBusy(true);
        setExportError('');
        try {
            await MentorshipService.exportCohortSessions(
                cohortId,
                {
                    search: appliedSearch,
                    campusId,
                    loggedByRole: loggedByRole || null,
                    rating: ratingFilter || undefined,
                },
                instituteId
            );
        } catch (error) {
            setExportError(MentorshipService.getErrorMessage(error));
        } finally {
            setExportBusy(false);
        }
    };

    const loadPage = (nextPage: number, nextPageSize = pageSize) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
    };

    return (
        <div className="ms-sl">
            <div className="ms-sl-header">
                <div>
                    <h3 className="ms-sl-title">Session Log</h3>
                    <p className="ms-sl-sub">
                        {sessionCount} sessions across {pairCount} pairs
                    </p>
                </div>
                <button
                    type="button"
                    className="ms-sl-btn ms-sl-btn--green"
                    disabled={!cohortAvailable || exportBusy}
                    onClick={() => {
                        void handleExport();
                    }}>
                    <i className="bi bi-download" aria-hidden="true" />
                    {exportBusy ? 'Exporting...' : 'Export Log'}
                </button>
            </div>

            {!cohortAvailable && <div className="text-muted mb-3">Select a cohort to view Session Log.</div>}
            {errorMessage ? <div className="alert alert-danger">{errorMessage}</div> : null}
            {exportError ? <div className="alert alert-danger">{exportError}</div> : null}

            <fieldset disabled={!cohortAvailable}>
                <div className="ms-sl-filters">
                    <input
                        className="form-control ms-sl-search"
                        placeholder="Search mentor or mentee name"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                    />
                    <select
                        className="form-select ms-sl-select"
                        value={campusId ?? ''}
                        onChange={(event) =>
                            setCampusId(event.target.value ? Number(event.target.value) : null)
                        }
                        aria-label="Campus">
                        <option value="">All Campuses</option>
                        {campuses.map((campus) => (
                            <option key={campus.value} value={campus.value}>
                                {campus.label}
                            </option>
                        ))}
                    </select>
                    <select
                        className="form-select ms-sl-select"
                        value={loggedByRole}
                        onChange={(event) => setLoggedByRole(event.target.value)}
                        aria-label="Logged by">
                        <option value="">All logged by</option>
                        {SESSION_LOG_LOGGED_BY_ROLES.map((role) => (
                            <option key={role} value={role}>
                                {role === 'SUPER_ADMIN'
                                    ? 'Super Admin'
                                    : role === 'INSTITUTE_ADMIN'
                                      ? 'Institute Admin'
                                      : 'Mentor'}
                            </option>
                        ))}
                    </select>
                    <div className="ms-sl-select">
                        <select
                            className="form-select"
                            value={ratingFilter}
                            onChange={(event) =>
                                setRatingFilter(event.target.value as '' | 'rated' | 'unrated')
                            }
                            aria-label="Any Rating">
                            <option value="">Any Rating</option>
                            <option value="rated">Rated</option>
                            <option value="unrated">Unrated</option>
                        </select>
                    </div>
                </div>
            </fieldset>

            {cohortAvailable ? (
                <>
                    <MentorshipSessionLogTable
                        sessions={sessions}
                        isLoading={isLoading}
                        emptyMessage="No sessions logged yet"
                    />
                    <div className="ms-sl-pager">
                        <div className="small text-muted">
                            Page {page} of {totalPages || 1} · {total} sessions
                        </div>
                        <div className="d-flex gap-2">
                            <select
                                className="form-select form-select-sm"
                                style={{ width: 90 }}
                                value={pageSize}
                                onChange={(event) => loadPage(1, Number(event.target.value))}>
                                {SESSION_LOG_PAGE_SIZES.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={page <= 1 || isLoading}
                                onClick={() => loadPage(page - 1, pageSize)}>
                                Previous
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={page >= (totalPages || 1) || isLoading}
                                onClick={() => loadPage(page + 1, pageSize)}>
                                Next
                            </button>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
};

export default MentorshipSessionLog;
